import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import ConfirmModal from '../components/ConfirmModal';

const DEFAULT_STATE = {
  open: false,
  title: 'Confirmar',
  message: '',
  confirmLabel: 'Confirmar',
  cancelLabel: 'Cancelar',
  tone: 'primary',
};

const ConfirmDialogContext = createContext(null);

export const ConfirmDialogProvider = ({ children }) => {
  const [state, setState] = useState(DEFAULT_STATE);
  const resolverRef = useRef(null);

  const confirm = useCallback((options = {}) => {
    setState({ ...DEFAULT_STATE, ...options, open: true });
    return new Promise((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const handleResolve = useCallback((value) => {
    setState((prev) => ({ ...prev, open: false }));
    if (resolverRef.current) {
      resolverRef.current(value);
      resolverRef.current = null;
    }
  }, []);

  const contextValue = useMemo(() => confirm, [confirm]);

  return (
    <ConfirmDialogContext.Provider value={contextValue}>
      {children}
      <ConfirmModal
        isOpen={state.open}
        title={state.title}
        message={state.message}
        confirmLabel={state.confirmLabel}
        cancelLabel={state.cancelLabel}
        tone={state.tone}
        onConfirm={() => handleResolve(true)}
        onCancel={() => handleResolve(false)}
      />
    </ConfirmDialogContext.Provider>
  );
};

const useConfirmDialog = () => {
  const contextConfirm = useContext(ConfirmDialogContext);
  if (!contextConfirm) {
    throw new Error('useConfirmDialog must be used within ConfirmDialogProvider');
  }
  const ConfirmDialog = useCallback(() => null, []);
  return { confirm: contextConfirm, ConfirmDialog };
};

export default useConfirmDialog;
